import { ActionIcon, Box, Button } from '@mantine/core'
import { ColumnDef, Row } from '@tanstack/react-table'
import { Loading } from 'components/loading/loading'
import { Table } from 'components/table'
import { useTranslation } from 'react-i18next'
import { IconEdit, IconPlus, IconTrash, IconGripVertical } from '@tabler/icons-react'
import { AddPrice } from 'components/modal/price/add.price'
import { ReactNode, useState } from 'react'
import { EditPrice } from 'components/modal/price/edit.price'
import { Price } from 'api/dto/price'
import { useTradeshowPrices, useGlobalPrices, useUpdatePrice } from 'api/query/price'
import { useParams } from 'react-router-dom'
import { DeletePrice } from 'components/modal/price/delete.price'
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { showNotification } from '@mantine/notifications'

export function Prices() {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { tradeshowId } = useParams()
  const { data: prices, isLoading } = useTradeshowPrices(tradeshowId)
  const { data: globalPrices, isLoading: isLoadingGlobalPrices } = useGlobalPrices()
  const { mutateAsync: updatePrice } = useUpdatePrice()
  const [editPrice, setEditPrice] = useState<Price | undefined>()
  const [deletePrice, setDeletePrice] = useState<Price | undefined>()
  const [addPriceModalOpened, setAddPriceModalOpened] = useState(false)
  const [isReordering, setIsReordering] = useState(false)

  // Sort prices by order
  const sortedPrices = [...(prices || [])].sort((a, b) => {
    if (a.order === undefined && b.order === undefined) return 0
    if (a.order === undefined) return 1
    if (b.order === undefined) return -1
    return a.order - b.order
  })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortedPrices.findIndex((p) => p.id === active.id)
      const newIndex = sortedPrices.findIndex((p) => p.id === over.id)

      // Reorder the array
      const reorderedPrices = [...sortedPrices]
      const [movedItem] = reorderedPrices.splice(oldIndex, 1)
      reorderedPrices.splice(newIndex, 0, movedItem)

      // Update order values for all items
      const updates = reorderedPrices.map((price, index) => ({
        ...price,
        order: index,
      }))

      // Update all prices concurrently with error handling
      setIsReordering(true)
      try {
        await Promise.all(updates.map((price) => updatePrice({ tradeshowId: tradeshowId!, price })))
        showNotification({
          title: t('common.success'),
          message: t('price.order-updated'),
          color: 'green',
        })
      } catch (error) {
        showNotification({
          title: t('common.error.title'),
          message: t('common.error.message'),
          color: 'red',
        })
        console.error('Error updating price order:', error)
      } finally {
        setIsReordering(false)
      }
    }
  }

  const SortableRow = ({ id, children }: { id: string; children: ReactNode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }

    return (
      <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </tr>
    )
  }

  const columns: ColumnDef<Price>[] = [
    {
      id: 'drag-handle',
      header: '',
      cell: () => (
        <Box style={{ cursor: 'grab' }} aria-label={t('price.drag-to-reorder')}>
          <IconGripVertical size={20} />
        </Box>
      ),
      size: 40,
    },
    {
      id: 'name',
      header: t('price.name'),
      cell: (props) => {
        const row = props.row.original
        if (language === 'en') {
          return row.nameEn
        }

        return row.nameFr
      },
    },
    {
      id: 'description',
      header: t('price.description'),
      cell: (props) => {
        const row = props.row.original
        if (language === 'en') {
          return row.descriptionEn
        }

        return row.descriptionFr
      },
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: t('price.priceType'),
      cell: (props) => {
        const type = props.getValue()
        return <Box>{t(`price.type.${type as string}`)}</Box>
      },
    },
    {
      id: 'price',
      accessorKey: 'globalPriceId',
      header: t('price.price'),
      cell: (props) => {
        const price = globalPrices?.find((globalPrice) => globalPrice.id === props.getValue())
        const amount = price?.amount || 0
        const currency = price?.currency || 'CAD'
        return <Box>{`${amount} ${currency.toUpperCase()}`}</Box>
      },
    },
    {
      id: 'globalPriceId',
      accessorKey: 'globalPriceId',
      header: t('price.globalPriceId'),
      cell: (props) => {
        const price = globalPrices?.find((globalPrice) => globalPrice.id === props.getValue())
        if (!price) {
          return null
        }

        return <Box>{price.id}</Box>
      },
    },
    {
      id: 'globalPriceId',
      accessorKey: 'globalPriceId',
      header: t('price.globalPriceName'),
      cell: (props) => {
        const price = globalPrices?.find((globalPrice) => globalPrice.id === props.getValue())
        if (!price) {
          return null
        }

        return <Box>{price.name}</Box>
      },
    },
    {
      id: 'actions',
      header: t('price.actions'),
      cell: (props) => (
        <Box display="flex">
          <ActionIcon mr={5} variant="outline" size="md" onClick={() => setEditPrice(props.row.original)}>
            <IconEdit size="20px" />
          </ActionIcon>

          <ActionIcon color="red" mr={5} variant="outline" size="md" onClick={() => setDeletePrice(props.row.original)}>
            <IconTrash size="20px" />
          </ActionIcon>
        </Box>
      ),
    },
  ]

  if (isLoading || isLoadingGlobalPrices) {
    return <Loading height="calc(100vh - 235px)" size="md" />
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortedPrices.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <Table<Price>
            data={sortedPrices}
            columns={columns}
            renderRow={(row, children) => (
              <SortableRow key={row.id} id={(row.original as Price).id}>
                {children}
              </SortableRow>
            )}
            actionButtons={() => [
              <Button h={44} size="sm" leftIcon={<IconPlus size={20} />} color="custom-primary-color.0" onClick={() => setAddPriceModalOpened(true)} disabled={isReordering}>
                {t('price.create')}
              </Button>,
            ]}
          />
        </SortableContext>
      </DndContext>
      <AddPrice opened={addPriceModalOpened} onClose={() => setAddPriceModalOpened(false)} />
      <EditPrice opened={!!editPrice} price={editPrice} onClose={() => setEditPrice(undefined)} />
      <DeletePrice opened={!!deletePrice} price={deletePrice} onClose={() => setDeletePrice(undefined)} />
    </>
  )
}
